import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Database, 
  Download, 
  Upload, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Shield,
  Calendar,
  FileCheck,
  RotateCcw,
  Loader2,
  Trash2,
  FileText,
  Terminal,
  Clipboard,
  Key,
  Skull,
  Users,
  Building2,
  FileStack,
  TriangleAlert,
  Power
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { AutomatedBackupSettings } from "./automated-backup-settings";
import { BackupStorageCard } from "./backup-storage-card";
import type { BackupStorageInventory } from "@shared/backup-storage-contract";

interface BackupRecord {
  id: number;
  filename: string;
  dropboxPath: string;
  sizeBytes: number | null;
  checksum: string | null;
  status: 'pending' | 'success' | 'failed' | 'restoring' | 'restore_failed';
  type: 'manual' | 'scheduled';
  triggeredBy: number | null;
  duration: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

interface BackupStats {
  totalBackups: number | null;
  successfulBackups: number;
  failedBackups: number;
  totalStorageBytes: number | null;
  storageInventory?: BackupStorageInventory;
  lastSuccessfulBackup: BackupRecord | null;
  lastBackup: BackupRecord | null;
}

interface LastBackupStatus {
  success: boolean;
  completedAt: string;
  backupId: number;
  error?: string;
}

interface BackupStatus {
  isBackupRunning: boolean;
  isRestoreRunning: boolean;
  currentBackupId: number | null;
  currentRestoreBackupId: number | null;
  dropboxConnected: boolean;
  lastBackupStatus: LastBackupStatus | null;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; variant: 'outline'; icon: typeof Clock; className: string }> = {
    pending: { label: 'In Progress', variant: 'outline', icon: Clock, className: 'border-yellow-500 text-yellow-400' },
    success: { label: 'Success', variant: 'outline', icon: CheckCircle2, className: 'border-green-500 text-green-400' },
    failed: { label: 'Failed', variant: 'outline', icon: XCircle, className: 'border-red-500 text-red-400' },
    restoring: { label: 'Restoring', variant: 'outline', icon: RotateCcw, className: 'border-blue-500 text-blue-400' },
    restore_failed: { label: 'Restore Failed', variant: 'outline', icon: AlertTriangle, className: 'border-red-500 text-red-400' },
  };
  
  const config = configs[status] || { label: status || 'Unknown', variant: 'outline' as const, icon: Clock, className: 'border-gray-500 text-gray-400' };
  const Icon = config.icon;
  
  return (
    <Badge variant={config.variant} className={config.className}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
}

function TypeBadge({ type }: { type: string }) {
  const configs: Record<string, { label: string; className: string }> = {
    manual: { label: 'Manual', className: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
    scheduled: { label: 'Scheduled', className: 'bg-purple-500/20 text-purple-300 border-purple-500/50' },
    'catch-up': { label: 'Catch-Up', className: 'bg-teal-500/20 text-teal-300 border-teal-500/50' },
  };
  
  const config = configs[type] || { 
    label: type || 'Unknown', 
    className: 'bg-gray-500/20 text-gray-300 border-gray-500/50' 
  };
  
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

export function BackupSettings() {
  const { toast } = useToast();
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedBackupId, setSelectedBackupId] = useState<number | null>(null);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [verifyingBackupId, setVerifyingBackupId] = useState<number | null>(null);
  
  // Delete backup state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState<BackupRecord | null>(null);
  
  // DR Credentials download state
  const [showCredentialsWarning, setShowCredentialsWarning] = useState(false);
  const [isDownloadingCredentials, setIsDownloadingCredentials] = useState(false);

  // DANGER ZONE: Database deletion state
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [deleteScopes, setDeleteScopes] = useState<{
    auditLogs: boolean;
    content: boolean;
    clients: boolean;
    users: boolean;
    factoryReset: boolean;
  }>({
    auditLogs: false,
    content: false,
    clients: false,
    users: false,
    factoryReset: false,
  });
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmPhrase, setDeleteConfirmPhrase] = useState('');
  const [deletePreview, setDeletePreview] = useState<any>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Server restart state
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);

  const { data: backupStatus, refetch: refetchStatus } = useQuery<BackupStatus>({
    queryKey: ['/api/admin/backups/status'],
    refetchInterval: 5000,
  });

  const { data: backupStats, isLoading: statsLoading, refetch: refetchStats } = useQuery<BackupStats>({
    queryKey: ['/api/admin/backups/stats'],
  });

  // Track if we were just running a backup (to do final refresh after completion)
  const wasRunningRef = useRef(false);
  
  const { data: backupHistory, isLoading: historyLoading, refetch: refetchHistory } = useQuery<{ backups: BackupRecord[], total: number }>({
    queryKey: ['/api/admin/backups'],
    // Poll every 10 seconds to catch status changes even without active operation
    refetchInterval: 10000,
  });

  useEffect(() => {
    const isRunning = backupStatus?.isBackupRunning || backupStatus?.isRestoreRunning;
    
    if (isRunning) {
      wasRunningRef.current = true;
      const interval = setInterval(() => {
        refetchStatus();
        refetchHistory();
        refetchStats();
      }, 3000);
      return () => clearInterval(interval);
    } else if (wasRunningRef.current) {
      // Operation just completed - do one final refresh after a short delay
      wasRunningRef.current = false;
      const timeout = setTimeout(() => {
        refetchStatus();
        refetchHistory();
        refetchStats();
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [backupStatus?.isBackupRunning, backupStatus?.isRestoreRunning, refetchStatus, refetchHistory, refetchStats]);

  const triggerBackupMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', '/api/admin/backups/trigger');
    },
    onSuccess: () => {
      toast({
        title: "Backup started",
        description: "Database backup is now running in the background.",
      });
      refetchStatus();
      refetchHistory();
    },
    onError: (error: any) => {
      toast({
        title: "Backup failed to start",
        description: error.message || "Could not start the backup process.",
        variant: "destructive",
      });
    },
  });

  const restoreBackupMutation = useMutation({
    mutationFn: async (backupId: number) => {
      return apiRequest('POST', `/api/admin/backups/${backupId}/restore`, { confirmPhrase: 'OVERWRITE DATABASE' });
    },
    onSuccess: () => {
      toast({
        title: "Restore started",
        description: "Database restore is now running. A safety backup was created first.",
      });
      setShowRestoreDialog(false);
      setConfirmPhrase('');
      setSelectedBackupId(null);
      refetchStatus();
      refetchHistory();
    },
    onError: (error: any) => {
      toast({
        title: "Restore failed",
        description: error.message || "Could not start the restore process.",
        variant: "destructive",
      });
    },
  });

  const verifyBackupMutation = useMutation({
    mutationFn: async (backupId: number) => {
      setVerifyingBackupId(backupId);
      const response = await apiRequest('GET', `/api/admin/backups/${backupId}/verify`);
      return response.json();
    },
    onSuccess: (data: any) => {
      if (data.valid) {
        toast({
          title: "Backup verified",
          description: data.checksumMatch 
            ? "Checksum verification passed. Backup is intact."
            : "Backup exists but no checksum available for verification.",
        });
      } else {
        toast({
          title: "Backup verification failed",
          description: data.error || "Backup may be corrupted.",
          variant: "destructive",
        });
      }
      setVerifyingBackupId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Verification error",
        description: error.message || "Could not verify backup.",
        variant: "destructive",
      });
      setVerifyingBackupId(null);
    },
  });

  const downloadBackupMutation = useMutation({
    mutationFn: async (backupId: number) => {
      const response = await apiRequest('GET', `/api/admin/backups/${backupId}/download`);
      return response.json();
    },
    onSuccess: (data: any) => {
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
        toast({
          title: "Download started",
          description: `Link expires in ${data.expiresIn}.`,
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Download failed",
        description: error.message || "Could not generate download link.",
        variant: "destructive",
      });
    },
  });

  const deleteBackupMutation = useMutation({
    mutationFn: async ({ backupId, force }: { backupId: number; force?: boolean }) => {
      const url = force 
        ? `/api/admin/backups/${backupId}?force=true` 
        : `/api/admin/backups/${backupId}`;
      const response = await apiRequest('DELETE', url);
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Backup deleted",
        description: data.message || "Backup has been deleted successfully.",
      });
      setShowDeleteDialog(false);
      setBackupToDelete(null);
      refetchHistory();
      refetchStats();
    },
    onError: (error: any) => {
      toast({
        title: "Delete failed",
        description: error.message || "Could not delete backup.",
        variant: "destructive",
      });
      setShowDeleteDialog(false);
      setBackupToDelete(null);
    },
  });

  const handleDeleteBackup = (backup: BackupRecord) => {
    setBackupToDelete(backup);
    setShowDeleteDialog(true);
  };

  // Check if backup is stuck (pending for over 30 minutes) - for warning display
  const isBackupStuck = (backup: BackupRecord): boolean => {
    if (backup.status !== 'pending') return false;
    const backupAge = Date.now() - new Date(backup.createdAt).getTime();
    const thirtyMinutes = 30 * 60 * 1000;
    return backupAge > thirtyMinutes;
  };

  // Check if backup can be force-cancelled (pending for over 60 minutes)
  const canForceCancelBackup = (backup: BackupRecord): boolean => {
    if (backup.status !== 'pending') return false;
    const backupAge = Date.now() - new Date(backup.createdAt).getTime();
    const sixtyMinutes = 60 * 60 * 1000;
    return backupAge > sixtyMinutes;
  };

  // Cancel stuck backup mutation
  const cancelBackupMutation = useMutation({
    mutationFn: async (backupId: number) => {
      const response = await apiRequest('POST', `/api/admin/backups/${backupId}/cancel`);
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Backup cancelled",
        description: data.message || "Stuck backup has been marked as failed.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backups'] });
      setBackupToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: "Cancel failed",
        description: error.message || "Could not cancel the stuck backup.",
        variant: "destructive",
      });
    },
  });

  const confirmDeleteBackup = (force: boolean = false) => {
    if (backupToDelete) {
      deleteBackupMutation.mutate({ backupId: backupToDelete.id, force });
    }
  };

  const handleRestore = (backupId: number) => {
    setSelectedBackupId(backupId);
    setConfirmPhrase('');
    setShowRestoreDialog(true);
  };

  const confirmRestore = () => {
    if (confirmPhrase === 'OVERWRITE DATABASE' && selectedBackupId) {
      restoreBackupMutation.mutate(selectedBackupId);
    }
  };

  const isOperationRunning = backupStatus?.isBackupRunning || backupStatus?.isRestoreRunning;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="navy-card border-gray-600">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
              <Database className="h-4 w-4" />
              Database Backups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{backupStats?.totalBackups ?? (statsLoading ? "…" : "—")}</div>
            <p className="text-xs text-gray-500">
              {backupStats?.successfulBackups || 0} successful, {backupStats?.failedBackups || 0} failed
            </p>
          </CardContent>
        </Card>

        <BackupStorageCard inventory={backupStats?.storageInventory} loading={statsLoading} />

        <Card className="navy-card border-gray-600">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Last Backup
            </CardTitle>
          </CardHeader>
          <CardContent>
            {backupStats?.lastSuccessfulBackup ? (
              <>
                <div className="text-lg font-bold text-green-400">
                  {formatDistanceToNow(new Date(backupStats.lastSuccessfulBackup.createdAt), { addSuffix: true })}
                </div>
                <p className="text-xs text-gray-500">
                  {format(new Date(backupStats.lastSuccessfulBackup.createdAt), 'MMM d, yyyy HH:mm')}
                </p>
              </>
            ) : (
              <div className="text-lg font-bold text-gray-500">No backups yet</div>
            )}
          </CardContent>
        </Card>

        <Card className="navy-card border-gray-600">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-400 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {isOperationRunning ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                <span className="text-blue-400 font-medium text-sm">
                  {backupStatus?.isBackupRunning ? 'Backing up...' : 'Restoring...'}
                </span>
              </div>
            ) : backupStatus?.lastBackupStatus && 
                (Date.now() - new Date(backupStatus.lastBackupStatus.completedAt).getTime()) < 30000 ? (
              backupStatus.lastBackupStatus.success ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  <span className="text-green-400 font-medium text-sm">Success</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-400" />
                  <span className="text-red-400 font-medium text-sm">Failed</span>
                </div>
              )
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span className="text-green-400 font-medium text-sm">Ready</span>
              </div>
            )}
            <div className="flex items-center gap-2 pt-1 border-t border-gray-700">
              {backupStatus?.dropboxConnected ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
                  <span className="text-xs text-gray-400">Dropbox connected</span>
                </>
              ) : (
                <>
                  <XCircle className="h-3.5 w-3.5 text-red-400" />
                  <span className="text-xs text-red-400">Dropbox disconnected</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <AutomatedBackupSettings />

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Backup History</h3>
        <div className="flex gap-2">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    refetchHistory();
                    refetchStats();
                    refetchStatus();
                  }}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  data-testid="button-refresh-backups"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Refresh backup list</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={() => triggerBackupMutation.mutate()}
                  disabled={isOperationRunning || triggerBackupMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="button-trigger-backup"
                >
                  {triggerBackupMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Create Backup
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Start a new database backup to Dropbox</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <ScrollArea className="h-[400px] rounded-md border border-gray-600">
        {historyLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : backupHistory?.backups && backupHistory.backups.length > 0 ? (
          <div className="p-4 space-y-3">
            {backupHistory.backups.map((backup) => (
              <div
                key={backup.id}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50 border border-gray-700"
                data-testid={`backup-row-${backup.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={backup.status} />
                    <TypeBadge type={backup.type} />
                    <span className="text-sm text-gray-400">
                      {formatBytes(backup.sizeBytes)}
                    </span>
                    {backup.duration && (
                      <span className="text-xs text-gray-500">
                        ({backup.duration}s)
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-mono text-gray-300 truncate">
                    {backup.filename}
                  </div>
                  <div className="text-xs text-gray-500">
                    {format(new Date(backup.createdAt), 'MMM d, yyyy HH:mm:ss')}
                    {backup.checksum && (
                      <span className="ml-2 text-green-500/70">
                        <FileCheck className="h-3 w-3 inline mr-1" />
                        SHA-256
                      </span>
                    )}
                  </div>
                  {backup.errorMessage && (
                    <div className="text-xs text-red-400 mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {backup.errorMessage}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <TooltipProvider delayDuration={300}>
                    {backup.status === 'success' && (
                      <>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => verifyBackupMutation.mutate(backup.id)}
                              disabled={verifyingBackupId === backup.id}
                              className="text-gray-400 hover:text-white"
                              data-testid={`button-verify-${backup.id}`}
                            >
                              {verifyingBackupId === backup.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileCheck className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>Verify backup integrity</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadBackupMutation.mutate(backup.id)}
                              disabled={downloadBackupMutation.isPending}
                              className="text-gray-400 hover:text-white"
                              data-testid={`button-download-${backup.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>Download backup file</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestore(backup.id)}
                              disabled={isOperationRunning}
                              className="text-orange-400 hover:text-orange-300 hover:bg-orange-400/10"
                              data-testid={`button-restore-${backup.id}`}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>Restore database from this backup</p>
                          </TooltipContent>
                        </Tooltip>
                      </>
                    )}
                    {/* Delete button - available for all non-pending backups */}
                    {backup.status !== 'pending' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBackup(backup)}
                            disabled={deleteBackupMutation.isPending}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                            data-testid={`button-delete-${backup.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>Delete backup from Dropbox</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {/* Force Fail button - for stuck pending backups over 60 minutes */}
                    {canForceCancelBackup(backup) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => cancelBackupMutation.mutate(backup.id)}
                            disabled={cancelBackupMutation.isPending}
                            className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
                            data-testid={`button-force-fail-${backup.id}`}
                          >
                            {cancelBackupMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>Force fail stuck backup (over 60 min)</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TooltipProvider>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500">
            <Database className="h-8 w-8 mb-2" />
            <p>No backups found</p>
            <p className="text-xs">Create your first backup to get started</p>
          </div>
        )}
      </ScrollArea>

      {/* Emergency CLI Recovery Instructions - Admin Only */}
      <Separator className="my-6 bg-gray-600" />
      
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Terminal className="h-5 w-5 text-red-400" />
          <h3 className="text-lg font-semibold text-white">Emergency CLI Recovery</h3>
          <Badge variant="outline" className="border-red-500 text-red-400 text-xs">
            When Web UI is Unavailable
          </Badge>
        </div>
        <p className="text-sm text-gray-400">
          If you lose access to the admin account or the web interface is broken, use the Emergency Restore Wizard 
          from the command line. This standalone tool can restore the database even when the application won't start.
        </p>
        
        <Card className="navy-card border-gray-600">
          <CardContent className="pt-4 space-y-4">
            {/* Quick Steps */}
            <div className="space-y-3">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Clipboard className="h-4 w-4 text-cyan-400" />
                Quick Recovery Steps
              </h4>
              
              <div className="bg-gray-800/70 rounded-lg p-4 space-y-3 font-mono text-sm">
                <div className="flex items-start gap-3">
                  <span className="bg-cyan-600 text-white px-2 py-0.5 rounded text-xs font-bold">1</span>
                  <div>
                    <p className="text-gray-300">Open the Shell tab in Replit (bottom of screen)</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="bg-cyan-600 text-white px-2 py-0.5 rounded text-xs font-bold">2</span>
                  <div>
                    <p className="text-gray-300">Type this command and press Enter:</p>
                    <code className="block mt-1 bg-gray-900 text-green-400 px-3 py-2 rounded border border-gray-700">
                      npx tsx scripts/emergency-restore.ts
                    </code>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="bg-cyan-600 text-white px-2 py-0.5 rounded text-xs font-bold">3</span>
                  <div>
                    <p className="text-gray-300">Follow the wizard prompts</p>
                    <p className="text-gray-500 text-xs mt-1">
                      You'll need your <span className="text-yellow-400">offline_dr_credentials.json</span> file from the DR Checklist
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Download PDF Button */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                <p>Download detailed instructions for non-technical users + AI debugging context</p>
              </div>
              <Button
                variant="outline"
                className="border-green-500 text-green-400 hover:bg-green-500/10 hover:text-green-300"
                onClick={async () => {
                  try {
                    const response = await fetch('/api/admin/backups/recovery-guide.pdf', {
                      credentials: 'include'
                    });
                    if (!response.ok) {
                      throw new Error('Failed to download PDF');
                    }
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Emergency-Recovery-Guide.pdf';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (error) {
                    toast({
                      title: "Download Failed",
                      description: "Could not download the recovery guide. Please try again.",
                      variant: "destructive"
                    });
                  }
                }}
                data-testid="button-download-recovery-pdf"
              >
                <FileText className="h-4 w-4 mr-2" />
                Download Recovery Guide (PDF)
              </Button>
            </div>
            
            {/* Download Credentials Button */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                <p>Download offline credentials file for CLI recovery (contains sensitive tokens)</p>
              </div>
              <Button
                variant="outline"
                className="border-red-500 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                onClick={() => setShowCredentialsWarning(true)}
                data-testid="button-download-dr-credentials"
              >
                <Key className="h-4 w-4 mr-2" />
                Download DR Credentials
              </Button>
            </div>
            
            {/* Important Notes */}
            <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-yellow-400 font-medium text-sm">
                <AlertTriangle className="h-4 w-4" />
                Important
              </div>
              <ul className="text-xs text-gray-400 space-y-1 ml-6 list-disc">
                <li>The PDF does <span className="text-yellow-400">NOT</span> contain passwords or tokens for security</li>
                <li>Keep your <span className="text-yellow-400">offline_dr_credentials.json</span> file stored safely offline</li>
                <li>The wizard has built-in safety: it creates a backup before restoring and can rollback on failure</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent className="navy-card border-gray-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
              Restore Database
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              <p className="mb-4">
                This will <strong className="text-red-400">overwrite all current data</strong> with data from backup #{selectedBackupId}. 
                A safety backup will be created before the restore.
              </p>
              <div className="space-y-2">
                <Label htmlFor="confirmPhrase" className="text-gray-300">
                  Type <code className="bg-gray-700 px-2 py-0.5 rounded text-orange-400">OVERWRITE DATABASE</code> to confirm:
                </Label>
                <Input
                  id="confirmPhrase"
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  placeholder="Type confirmation phrase..."
                  className="bg-gray-800 border-gray-600 text-white"
                  data-testid="input-confirm-restore"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white border-gray-600 hover:bg-gray-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRestore}
              disabled={confirmPhrase !== 'OVERWRITE DATABASE' || restoreBackupMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="button-confirm-restore"
            >
              {restoreBackupMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Restore Database
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Backup Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="navy-card border-gray-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Delete Backup
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              <p className="mb-2">
                Are you sure you want to delete this backup?
              </p>
              {backupToDelete && (
                <div className="bg-gray-800 rounded-md p-3 mt-3 border border-gray-600">
                  <p className="font-mono text-sm text-gray-300">{backupToDelete.filename}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {format(new Date(backupToDelete.createdAt), 'MMM d, yyyy HH:mm:ss')} • {formatBytes(backupToDelete.sizeBytes)}
                  </p>
                  {backupToDelete && isBackupStuck(backupToDelete) && (
                    <p className="text-xs text-yellow-400 mt-2">
                      This backup appears stuck (in progress for over 30 minutes). You can force-delete it.
                    </p>
                  )}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-gray-700 text-white border-gray-600 hover:bg-gray-600"
              onClick={() => {
                setBackupToDelete(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            {backupToDelete && isBackupStuck(backupToDelete) ? (
              <AlertDialogAction
                onClick={() => confirmDeleteBackup(true)}
                disabled={deleteBackupMutation.isPending}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="button-force-delete"
              >
                {deleteBackupMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <AlertTriangle className="h-4 w-4 mr-2" />
                )}
                Force Delete
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={() => confirmDeleteBackup(false)}
                disabled={deleteBackupMutation.isPending || !!(backupToDelete && backupToDelete.status === 'pending')}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="button-confirm-delete"
              >
                {deleteBackupMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete Backup
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DR Credentials Download Warning Dialog */}
      <AlertDialog open={showCredentialsWarning} onOpenChange={setShowCredentialsWarning}>
        <AlertDialogContent className="navy-card border-gray-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-400" />
              Security Warning - Sensitive Credentials
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 space-y-4">
              <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-3">
                <p className="text-red-300 font-medium text-sm">
                  This file contains your Dropbox API credentials with full access to your backup storage.
                </p>
              </div>
              
              <div className="space-y-2 text-sm">
                <p className="font-medium text-white">Before downloading, ensure you understand:</p>
                <ul className="list-disc ml-5 space-y-1 text-gray-400">
                  <li>Store this file in a <span className="text-yellow-400">password manager</span> or <span className="text-yellow-400">encrypted storage</span></li>
                  <li>Never commit to version control or share via email/chat</li>
                  <li>Delete any unencrypted copies after secure storage</li>
                  <li>If compromised, revoke tokens in Dropbox App Console immediately</li>
                </ul>
              </div>
              
              <div className="text-xs text-gray-500 border-t border-gray-700 pt-3">
                <p>The file is required for the Emergency CLI Recovery Wizard when the web interface is unavailable.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white border-gray-600 hover:bg-gray-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setIsDownloadingCredentials(true);
                try {
                  const response = await fetch('/api/admin/dr-credentials/download', {
                    credentials: 'include'
                  });
                  if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || 'Failed to download credentials');
                  }
                  
                  const contentDisposition = response.headers.get('Content-Disposition');
                  let filename = 'offline_dr_credentials.json';
                  if (contentDisposition) {
                    const match = contentDisposition.match(/filename="(.+)"/);
                    if (match) filename = match[1];
                  }
                  
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                  
                  toast({
                    title: "Credentials Downloaded",
                    description: "Store the file securely offline. Delete any unencrypted copies.",
                  });
                  setShowCredentialsWarning(false);
                } catch (error: any) {
                  toast({
                    title: "Download Failed",
                    description: error.message || "Could not download credentials. Please try again.",
                    variant: "destructive"
                  });
                } finally {
                  setIsDownloadingCredentials(false);
                }
              }}
              disabled={isDownloadingCredentials}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="button-confirm-download-credentials"
            >
              {isDownloadingCredentials ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              I Understand - Download Credentials
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ============================================
          SERVER RESTART - Memory Cleanup
          ============================================ */}
      <Card className="border-orange-700/50 bg-orange-950/20 mt-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-orange-400 flex items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <Power className="h-4 w-4" />
              Server Restart - Memory Cleanup
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-orange-900/30 border border-orange-700/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className="text-orange-300 font-medium">Free up server memory</p>
                <p className="text-sm text-gray-400">
                  Restart the server to release all cached memory. This is the only way to guarantee a 100% memory flush.
                  All connected users will be briefly disconnected (2-5 seconds) while the server restarts.
                </p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={() => setShowRestartConfirm(true)}
            disabled={isRestarting}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="button-restart-server"
          >
            {isRestarting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Restarting...
              </>
            ) : (
              <>
                <Power className="h-4 w-4 mr-2" />
                Restart Server
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Restart Server Confirmation Dialog */}
      <AlertDialog open={showRestartConfirm} onOpenChange={setShowRestartConfirm}>
        <AlertDialogContent className="bg-[#1a1f2e] border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Power className="h-5 w-5 text-orange-400" />
              Restart Server?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will restart the server to free up memory. All connected users will be briefly 
              disconnected (2-5 seconds) and will need to refresh their browser.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-gray-700 text-white hover:bg-gray-600 border-gray-600"
              data-testid="button-cancel-restart"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setIsRestarting(true);
                try {
                  await apiRequest('POST', '/api/admin/restart');
                  toast({
                    title: "Server restarting",
                    description: "The page will reload automatically in a few seconds.",
                  });
                  // Wait a bit then reload the page
                  setTimeout(() => {
                    window.location.reload();
                  }, 3000);
                } catch (error: any) {
                  toast({
                    title: "Restart failed",
                    description: error.message || "Could not restart the server.",
                    variant: "destructive",
                  });
                  setIsRestarting(false);
                }
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="button-confirm-restart"
            >
              <Power className="h-4 w-4 mr-2" />
              Restart Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ============================================
          DANGER ZONE - Database Deletion
          ============================================ */}
      <Card className="border-red-700/50 bg-red-950/20 mt-8">
        <CardHeader 
          className="pb-2 cursor-pointer hover:bg-red-900/20 transition-colors"
          onClick={() => setShowDangerZone(!showDangerZone)}
        >
          <CardTitle className="text-sm font-medium text-red-400 flex items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <Skull className="h-4 w-4" />
              Danger Zone - Database Deletion
            </div>
            <span className="text-xs text-gray-500">{showDangerZone ? '▲ Hide' : '▼ Expand'}</span>
          </CardTitle>
        </CardHeader>
        
        {showDangerZone && (
          <CardContent className="space-y-6 border-t border-red-700/30 pt-4">
            <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <TriangleAlert className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-2">
                  <p className="text-red-300 font-medium">Permanent Deletion Warning</p>
                  <p className="text-sm text-gray-400">
                    This action permanently deletes data from the database. This cannot be undone unless you have a backup. 
                    For Factory Reset, all users including your account will be deleted, triggering Recovery Mode.
                  </p>
                </div>
              </div>
            </div>

            {/* Scope Selection */}
            <div className="space-y-3">
              <Label className="text-white text-sm font-medium">Select what to delete:</Label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                  <Checkbox 
                    id="delete-audit"
                    checked={deleteScopes.auditLogs}
                    onCheckedChange={(checked) => setDeleteScopes(prev => ({ ...prev, auditLogs: !!checked }))}
                    disabled={deleteScopes.factoryReset}
                    data-testid="checkbox-delete-audit"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="delete-audit" className="text-white text-sm cursor-pointer flex items-center gap-2">
                      <FileStack className="h-4 w-4 text-gray-400" />
                      Audit Logs
                    </Label>
                    <p className="text-xs text-gray-500">Delete all audit logs and project audit logs</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                  <Checkbox 
                    id="delete-content"
                    checked={deleteScopes.content}
                    onCheckedChange={(checked) => {
                      setDeleteScopes(prev => ({ 
                        ...prev, 
                        content: !!checked,
                        // If unchecking content, also uncheck clients (dependency)
                        clients: checked ? prev.clients : false
                      }));
                    }}
                    disabled={deleteScopes.factoryReset}
                    data-testid="checkbox-delete-content"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="delete-content" className="text-white text-sm cursor-pointer flex items-center gap-2">
                      <Database className="h-4 w-4 text-orange-400" />
                      Projects & Content
                    </Label>
                    <p className="text-xs text-gray-500">Delete all projects, pulses, tasks, messages, time entries, and Dropbox files</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                  <Checkbox 
                    id="delete-clients"
                    checked={deleteScopes.clients}
                    onCheckedChange={(checked) => setDeleteScopes(prev => ({ ...prev, clients: !!checked }))}
                    disabled={deleteScopes.factoryReset || !deleteScopes.content}
                    data-testid="checkbox-delete-clients"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="delete-clients" className={`text-sm cursor-pointer flex items-center gap-2 ${deleteScopes.content ? 'text-white' : 'text-gray-500'}`}>
                      <Building2 className="h-4 w-4 text-yellow-400" />
                      Clients
                    </Label>
                    <p className="text-xs text-gray-500">
                      {deleteScopes.content 
                        ? 'Delete all clients and contact persons'
                        : 'Requires "Projects & Content" to be selected first'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                  <Checkbox 
                    id="delete-users"
                    checked={deleteScopes.users}
                    onCheckedChange={(checked) => setDeleteScopes(prev => ({ ...prev, users: !!checked }))}
                    disabled={deleteScopes.factoryReset}
                    data-testid="checkbox-delete-users"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="delete-users" className="text-white text-sm cursor-pointer flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-400" />
                      Other Users
                    </Label>
                    <p className="text-xs text-gray-500">Delete all users except your account (preserves your admin access)</p>
                  </div>
                </div>
              </div>

              <Separator className="bg-red-700/50 my-4" />

              <div className="flex items-start gap-3 p-4 rounded-lg bg-red-900/40 border border-red-600/50">
                <Checkbox 
                  id="delete-factory"
                  checked={deleteScopes.factoryReset}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setDeleteScopes({
                        auditLogs: true,
                        content: true,
                        clients: true,
                        users: true,
                        factoryReset: true,
                      });
                    } else {
                      setDeleteScopes({
                        auditLogs: false,
                        content: false,
                        clients: false,
                        users: false,
                        factoryReset: false,
                      });
                    }
                  }}
                  data-testid="checkbox-delete-factory"
                />
                <div className="space-y-1">
                  <Label htmlFor="delete-factory" className="text-red-300 text-sm cursor-pointer flex items-center gap-2 font-medium">
                    <Skull className="h-4 w-4 text-red-400" />
                    Factory Reset (Delete EVERYTHING)
                  </Label>
                  <p className="text-xs text-red-400/80">
                    Deletes ALL data including your account, settings, years, and all system configuration. 
                    Triggers Recovery Mode — check the server logs for the one-time recovery password.
                  </p>
                </div>
              </div>
            </div>

            {/* Preview Button */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={async () => {
                  const selectedScopes: string[] = [];
                  if (deleteScopes.factoryReset) {
                    selectedScopes.push('factoryReset');
                  } else {
                    if (deleteScopes.auditLogs) selectedScopes.push('auditLogs');
                    if (deleteScopes.content) selectedScopes.push('content');
                    if (deleteScopes.clients) selectedScopes.push('clients');
                    if (deleteScopes.users) selectedScopes.push('users');
                  }
                  
                  if (selectedScopes.length === 0) {
                    toast({
                      title: "No scope selected",
                      description: "Please select at least one category to delete.",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  setIsLoadingPreview(true);
                  setDeletePreview(null);
                  
                  try {
                    const response = await apiRequest('POST', '/api/admin/delete-database/preview', { scopes: selectedScopes });
                    const data = await response.json();
                    setDeletePreview(data);
                  } catch (error: any) {
                    toast({
                      title: "Preview failed",
                      description: error.message || "Could not load preview.",
                      variant: "destructive",
                    });
                  } finally {
                    setIsLoadingPreview(false);
                  }
                }}
                disabled={isLoadingPreview || isDeleting || (!deleteScopes.auditLogs && !deleteScopes.content && !deleteScopes.clients && !deleteScopes.users && !deleteScopes.factoryReset)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                data-testid="button-preview-deletion"
              >
                {isLoadingPreview ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileCheck className="h-4 w-4 mr-2" />
                )}
                Preview Deletion
              </Button>
            </div>

            {/* Preview Results */}
            {deletePreview && (
              <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-white mb-3">Records to be deleted:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {deletePreview.projects?.count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Projects:</span>
                      <span className="text-orange-400 font-mono">{deletePreview.projects.count}</span>
                    </div>
                  )}
                  {deletePreview.pulses?.count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pulses:</span>
                      <span className="text-orange-400 font-mono">{deletePreview.pulses.count}</span>
                    </div>
                  )}
                  {deletePreview.tasks?.count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tasks:</span>
                      <span className="text-orange-400 font-mono">{deletePreview.tasks.count}</span>
                    </div>
                  )}
                  {deletePreview.chatMessages?.count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Messages:</span>
                      <span className="text-orange-400 font-mono">{deletePreview.chatMessages.count}</span>
                    </div>
                  )}
                  {deletePreview.timeEntries?.count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Time Entries:</span>
                      <span className="text-orange-400 font-mono">{deletePreview.timeEntries.count}</span>
                    </div>
                  )}
                  {deletePreview.clients?.count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Clients:</span>
                      <span className="text-yellow-400 font-mono">{deletePreview.clients.count}</span>
                    </div>
                  )}
                  {deletePreview.users?.count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Users:</span>
                      <span className="text-blue-400 font-mono">{deletePreview.users.count}</span>
                    </div>
                  )}
                  {deletePreview.auditLogs?.count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Audit Logs:</span>
                      <span className="text-gray-400 font-mono">{deletePreview.auditLogs.count}</span>
                    </div>
                  )}
                  {deletePreview.files?.count > 0 && (
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-400">Dropbox Files:</span>
                      <span className="text-red-400 font-mono">{deletePreview.files.count}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Password & Confirmation */}
            <div className="space-y-4 pt-4 border-t border-gray-700">
              <div className="space-y-2">
                <Label htmlFor="delete-password" className="text-white text-sm">Confirm your password:</Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your admin password"
                  className="bg-gray-800 border-gray-600 text-white max-w-sm"
                  data-testid="input-delete-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-confirm" className="text-white text-sm">
                  Type <code className="text-red-400 bg-red-900/30 px-1.5 py-0.5 rounded">{deleteScopes.factoryReset ? 'DELETE EVERYTHING' : 'DELETE'}</code> to confirm:
                </Label>
                <Input
                  id="delete-confirm"
                  type="text"
                  value={deleteConfirmPhrase}
                  onChange={(e) => setDeleteConfirmPhrase(e.target.value)}
                  placeholder={deleteScopes.factoryReset ? 'DELETE EVERYTHING' : 'DELETE'}
                  className="bg-gray-800 border-gray-600 text-white max-w-sm font-mono"
                  data-testid="input-delete-confirm"
                />
              </div>

              <Button
                variant="destructive"
                onClick={async () => {
                  const selectedScopes: string[] = [];
                  if (deleteScopes.factoryReset) {
                    selectedScopes.push('factoryReset');
                  } else {
                    if (deleteScopes.auditLogs) selectedScopes.push('auditLogs');
                    if (deleteScopes.content) selectedScopes.push('content');
                    if (deleteScopes.clients) selectedScopes.push('clients');
                    if (deleteScopes.users) selectedScopes.push('users');
                  }
                  
                  if (selectedScopes.length === 0) {
                    toast({
                      title: "No scope selected",
                      description: "Please select at least one category to delete.",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  if (!deletePassword) {
                    toast({
                      title: "Password required",
                      description: "Please enter your admin password to confirm.",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  const expectedPhrase = deleteScopes.factoryReset ? 'DELETE EVERYTHING' : 'DELETE';
                  if (deleteConfirmPhrase !== expectedPhrase) {
                    toast({
                      title: "Confirmation phrase incorrect",
                      description: `Please type "${expectedPhrase}" exactly to confirm.`,
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  setIsDeleting(true);
                  
                  try {
                    const response = await apiRequest('POST', '/api/admin/delete-database', {
                      scopes: selectedScopes,
                      password: deletePassword,
                      confirmationPhrase: deleteConfirmPhrase,
                    });
                    const result = await response.json();
                    
                    if (result.success) {
                      toast({
                        title: "Deletion complete",
                        description: result.triggersRecoveryMode 
                          ? "Factory reset complete. Redirecting to login..."
                          : `Successfully deleted selected data.`,
                      });
                      
                      // Reset form
                      setDeleteScopes({
                        auditLogs: false,
                        content: false,
                        clients: false,
                        users: false,
                        factoryReset: false,
                      });
                      setDeletePassword('');
                      setDeleteConfirmPhrase('');
                      setDeletePreview(null);
                      
                      // Note: WebSocket will broadcast system.reset which triggers queryClient.clear() and redirect
                    } else {
                      throw new Error(result.errors?.join(', ') || 'Deletion failed');
                    }
                  } catch (error: any) {
                    toast({
                      title: "Deletion failed",
                      description: error.message || "Could not complete the deletion.",
                      variant: "destructive",
                    });
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                disabled={
                  isDeleting || 
                  !deletePassword || 
                  deleteConfirmPhrase !== (deleteScopes.factoryReset ? 'DELETE EVERYTHING' : 'DELETE') ||
                  (!deleteScopes.auditLogs && !deleteScopes.content && !deleteScopes.clients && !deleteScopes.users && !deleteScopes.factoryReset)
                }
                className="bg-red-700 hover:bg-red-800"
                data-testid="button-execute-deletion"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Skull className="h-4 w-4 mr-2" />
                    {deleteScopes.factoryReset ? 'Execute Factory Reset' : 'Execute Deletion'}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
